import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialShareButtons = ({ referralLink, referralCode }) => {
  const [showQR, setShowQR] = useState(false);

  const shareMessage = `If MyBodyToday helped you track your symptoms, share it with someone who might benefit too. Use my link to get started: ${referralLink}`;

  const handleShare = (platform) => {
    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent('Track your health with MyBodyToday')}&body=${encodeURIComponent(shareMessage)}`;
        break;
      default:
        return;
    }
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralLink)}`;

  const socialPlatforms = [
    { name: 'WhatsApp', icon: 'MessageCircle', color: 'bg-green-500 hover:bg-green-600', platform: 'whatsapp' },
    { name: 'Facebook', icon: 'Facebook', color: 'bg-blue-600 hover:bg-blue-700', platform: 'facebook' },
    { name: 'LinkedIn', icon: 'Linkedin', color: 'bg-blue-700 hover:bg-blue-800', platform: 'linkedin' },
    { name: 'Email', icon: 'Mail', color: 'bg-gray-600 hover:bg-gray-700', platform: 'email' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Share2" size={24} className="text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Share with someone who needs it</h2>
          <p className="text-sm text-muted-foreground">Help others track their health</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {socialPlatforms?.map((platform) => (
          <button
            key={platform?.platform}
            onClick={() => handleShare(platform?.platform)}
            className={`flex flex-col items-center gap-2 p-4 rounded-lg ${platform?.color} text-white transition-colors`}
          >
            <Icon name={platform?.icon} size={22} />
            <span className="text-xs font-semibold">{platform?.name}</span>
          </button>
        ))}
      </div>
      {/* QR Code */}
      <div className="border-t border-gray-200 pt-4">
        <button
          onClick={() => setShowQR(!showQR)}
          className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          <Icon name="QrCode" size={18} />
          {showQR ? 'Hide QR Code' : 'Show QR Code'}
        </button>

        {showQR && (
          <div className="mt-4 flex flex-col items-center gap-3">
            <img
              src={qrUrl}
              alt="Referral QR code — scan to open MyBodyToday referral link"
              className="w-48 h-48 rounded-lg border border-gray-200 shadow-sm"
            />
            <p className="text-xs text-muted-foreground text-center">Scan to open your referral link</p>
            <a
              href={qrUrl}
              download="mybodytoday-referral-qr.png"
              className="text-xs text-primary hover:underline font-medium"
            >
              Download QR code
            </a>
          </div>
        )}
      </div>
      {/* Native Share (mobile) */}
      {navigator?.share && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            fullWidth
            iconName="Share2"
            onClick={() => {
              navigator.share({
                title: 'Help others track their health',
                text: shareMessage,
                url: referralLink
              });
            }}
          >
            Share via...
          </Button>
        </div>
      )}
    </div>
  );
};

export default SocialShareButtons;